import { Component, Input, OnInit, SimpleChanges } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AgGridAngular } from "ag-grid-angular";
import {
    UntypedFormGroup,
    FormsModule,
    ReactiveFormsModule,
} from "@angular/forms";

import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    GridApi,
    ColDef,
    TextEditorModule,
    CellEditingStartedEvent,
    CellEditingStoppedEvent,
    NumberEditorModule,
    RowStyleModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
    NewValueParams,
    ScrollApiModule,
    HighlightChangesModule
} from "ag-grid-community";

import {
    NgbModal,
    NgbModalOptions,
    NgbActiveModal,
} from "@ng-bootstrap/ng-bootstrap";


import { CreateSowModalComponent } from "./components/create-sow-modal/create-sow-modal.component";
import { SowApiService } from "src/app/core/api/sow-api.service";
import { Sow } from "src/app/core/models/sow";
import { formatDate } from "@angular/common";
import { ToastrService } from "ngx-toastr";
import { ActionsComponent } from "./components/actions/actions.component";
import { take } from "rxjs";
import { SpinnerService } from "src/app/core/services/spinner.service";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    TextEditorModule,
    NumberEditorModule,
    RowStyleModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
    ScrollApiModule,
    HighlightChangesModule

]);

@Component({
    selector: "app-sow",
    standalone: true,
    imports: [AgGridAngular, TranslatePipe, FormsModule, ReactiveFormsModule],
    templateUrl: "./sow.component.html",
    styleUrl: "./sow.component.scss",
})
export class SowComponent implements OnInit {
    @Input() contractId: number;
    public SowDataForm: UntypedFormGroup;
    public isCellEditing = false;
    getRowStyle: any;
    gridApi: GridApi<Sow>;
    activeModal: NgbActiveModal;
    // public contractId: number;
    public SowList: Sow[];
    public sowGridTitles: any = {};
    public colDefs: ColDef<Sow>[] = [];

    constructor(
        private route: ActivatedRoute,
        private modalService: NgbModal,
        private sowApiService: SowApiService,
        private translateService: TranslateService,
        private toastr: ToastrService,
        private spinner: SpinnerService
    ) {}

    public validationOnEmptyCell(event: NewValueParams<Sow, any, any>) {
        // Revert to previous value
        event.data[event.colDef.field as any] = event.oldValue;
        this.gridApi.refreshCells({ force: true });

        // Keep focus on the current cell (don't move to next tab)
        this.gridApi.startEditingCell({
            rowIndex: event.node.rowIndex,
            colKey: event.colDef.field,
        });
    }

    //Function to  update fields in SOW Ag grid
    public updateField(eventData: NewValueParams<Sow, any, any>) {
        this.sowApiService
            .updateSow(eventData.data)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.translateService
                        .get("sow.form.updateSow", {
                            field: eventData.colDef.headerName,
                        })
                        .subscribe((res) => this.toastr.success(res));
                },
                error: () => {
                    this.translateService
                        .get("sow.form.updateError", {
                            field: eventData.colDef.headerName,
                        })
                        .subscribe((res) => this.toastr.error(res));
                },
            });
    }

    defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true, // Adds filter input fields at the top
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
    };

    autoGroupColumnDef: ColDef = {
        minWidth: 200,
    };

    gridContext = {
        parentComponent: this,
    };

    private initializeColumnDefs() {
        this.colDefs = [
            {
                field: "createdDate",
                headerName: this.sowGridTitles.createdDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 125,
                sort: "desc",
            },
            {
                field: "startDate",
                headerName: this.sowGridTitles.startDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 115,
            },
            {
                field: "endDate",
                headerName: this.sowGridTitles.endDate,
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width: 120,
            },
            {
                field: "po",
                headerName: this.sowGridTitles.poNumber,
                filter: "agTextColumnFilter",
                editable: true,
                onCellValueChanged: (event) => {
                    if (event.newValue === null) {
                        this.toastr.error("This cell cannot be empty");
                        this.validationOnEmptyCell(event);
                        return;
                    }
                    this.updateField(event);
                },
            },
            {
                field: "caseNumber",
                headerName: this.sowGridTitles.caseNumber,
                editable: true,
                onCellValueChanged: (event) => {
                    this.updateField(event);
                },
            },
            {
                field: "budget",
                headerName: this.sowGridTitles.budget,
                editable: true,
                onCellValueChanged: (event) => {
                    this.updateField(event);
                },
            },
            {
                field: "comment",
                headerName: this.sowGridTitles.comment,
                filter: "agTextColumnFilter",
                flex: 1,
                editable: true,
                resizable: false,
                onCellValueChanged: (event) => {
                    this.updateField(event);
                },
            },
            {
                headerName: "Actions",
                cellRenderer: ActionsComponent,
                cellRendererParams: {
                    context: this.gridContext,
                },
                filter: false,
                width: 120,
                pinned: "right",
                resizable: false,
                lockPinned: true,
                lockPosition: true,
            },
        ];
    }

    ngOnInit(): void {
        this.translateService.stream("sow.form").subscribe((res) => {
            this.sowGridTitles = res;
            this.initializeColumnDefs();
        });
        this.loadSowList();
    }

    ngOnChanges(change: SimpleChanges){
       if(change) {
       this.loadSowList();

       }


    }

    private loadSowList() {
        // this.spinner.show();
        this.sowApiService
            .getAllSowByContractId(this.contractId)
            .subscribe({
                next: (result) => {
                    this.SowList = result;
                    // this.spinner.hide();
                }
            })
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
        this.loadInitialData();
    }

    loadInitialData(): void {
        this.gridApi.setGridOption("rowData", this.SowList);
    }

    onCellEditingStarted(event: CellEditingStartedEvent) {
        this.isCellEditing = true;

        this.getRowStyle = (params: any) => {
            if (params.node.id === event.node.id) {
                return { background: "#b0d7efc9" };
            }

            return null;
        };
    }
    onCellEditingStopped(event: CellEditingStoppedEvent) {
        this.isCellEditing = false;
        this.getRowStyle = (params: any) => {
            if (params.node.id === event.node.id) {
                return { background: "" };
            }

            return null;
        };
    }

    onSave() {
        this.activeModal.close("the modal is closed succussfully");
    }

    public openModal() {
        const options: NgbModalOptions = {
            centered: true,
            backdrop: true,
            size: "lg",
        };


        const modalRef = this.modalService.open(
            CreateSowModalComponent,
            options
        );
        modalRef.componentInstance.contractId = Number(this.contractId);

        modalRef.result.then(
            (result) => {
              this.gridApi.applyTransaction({
                    add: [result],
                });

            },
            (reason) => {}
        );
    }
}
